import React, { useState } from 'react';
import { CopyIcon, CheckIcon, QuoteIcon, RefreshCwIcon, PencilIcon, LightbulbIcon } from 'lucide-react';
interface OutputData {
  mentalModels: string[];
  reframeText: string;
  quote: {
    text: string;
    source: string;
  };
  socraticQuestions: string[];
}
interface OutputStepProps {
  data: OutputData;
  originalInput: string;
  onEdit: () => void;
  onStartFresh: () => void;
}
export function OutputStep({
  data,
  originalInput,
  onEdit,
  onStartFresh
}: OutputStepProps) {
  const [copied, setCopied] = useState(false);
  const formatOutputForCopy = () => {
    const lines = ['=== Naval Lens Reframe ===', '', 'Original Decision:', originalInput, '', 'Mental Models Applied:', data.mentalModels.join(' · '), '', 'Reframe:', data.reframeText, '', 'Naval Quote:', `"${data.quote.text}"`, `— ${data.quote.source}`, '', 'Socratic Questions:', ...data.socraticQuestions.map((q, i) => `${i + 1}. ${q}`)];
    return lines.join('\n');
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatOutputForCopy());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  return <div className="w-full max-w-2xl mx-auto" data-id="element-40">
      {/* Mental Model Badges */}
      <div className="flex flex-wrap gap-2 mb-6" data-id="element-41">
        {data.mentalModels.map(model => <span key={model} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-accent text-accent-foreground" data-id="element-42">
            {model}
          </span>)}
      </div>

      {/* Reframe Section */}
      <div className="mb-8" data-id="element-43">
        <div className="flex items-center gap-2 mb-3" data-id="element-44">
          <LightbulbIcon className="w-5 h-5 text-foreground" data-id="element-45" />
          <h2 className="text-lg font-semibold text-foreground" data-id="element-46">Reframe</h2>
        </div>
        <p className="text-foreground text-lg leading-relaxed" data-id="element-47">
          {data.reframeText}
        </p>
      </div>

      {/* Naval Quote Card */}
      <div className="bg-card border border-border rounded-lg p-6 mb-8" data-id="element-48">
        <div className="flex items-start gap-3" data-id="element-49">
          <QuoteIcon className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-1" data-id="element-50" />
          <div data-id="element-51">
            <blockquote className="text-foreground text-lg italic leading-relaxed mb-2" data-id="element-52">
              "{data.quote.text}"
            </blockquote>
            <cite className="text-muted-foreground text-sm not-italic" data-id="element-53">
              — {data.quote.source}
            </cite>
          </div>
        </div>
      </div>

      {/* Socratic Questions */}
      <div className="mb-8" data-id="element-54">
        <h3 className="text-lg font-semibold text-foreground mb-4" data-id="element-55">
          Questions to consider
        </h3>
        <ol className="space-y-3" data-id="element-56">
          {data.socraticQuestions.map((question, index) => <li key={index} className="flex items-start gap-3 text-foreground" data-id="element-57">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground" data-id="element-58">
                {index + 1}
              </span>
              <span className="leading-relaxed" data-id="element-59">{question}</span>
            </li>)}
        </ol>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border" data-id="element-60">
        <button type="button" onClick={handleCopy} className="flex-1 py-3 px-6 rounded-lg font-medium text-base bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.98] transition-all duration-200 inline-flex items-center justify-center gap-2" aria-label={copied ? 'Copied to clipboard' : 'Copy full output'} data-id="element-61">
          {copied ? <>
              <CheckIcon className="w-4 h-4" data-id="element-62" />
              Copied!
            </> : <>
              <CopyIcon className="w-4 h-4" data-id="element-63" />
              Copy
            </>}
        </button>

        <button type="button" onClick={onEdit} className="py-3 px-6 rounded-lg font-medium text-base border border-border text-foreground hover:bg-muted transition-colors inline-flex items-center justify-center gap-2" aria-label="Edit original input" data-id="element-64">
          <PencilIcon className="w-4 h-4" data-id="element-65" />
          Edit
        </button>

        <button type="button" onClick={onStartFresh} className="py-3 px-6 rounded-lg font-medium text-base text-muted-foreground hover:text-foreground hover:bg-muted transition-colors inline-flex items-center justify-center gap-2" aria-label="Start fresh with new decision" data-id="element-66">
          <RefreshCwIcon className="w-4 h-4" data-id="element-67" />
          Start Fresh
        </button>
      </div>
    </div>;
}